import { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { spinner } from "../../Assets";

const WheelComponent = ({
  segments,
  segColors,
  segImg,
  buttonIcon,
  logoIcon,
  result,
  data,
  frame,
  segSize,
  BackDataSize,
  onFinished,
  primaryColor = "black",
  contrastColor = "white",
  buttonText = "Spin",
  isOnlyOnce = true,
  size = 180,
  upDuration = 100,
  downDuration = 50200,
  fontFamily = "proxima-nova",
  segmentFontSize = "1em",
  imgSize = 130,
  imgX = 25,
  imgY = -25,
  imgCenterX = 20,
  imgCenterY = 20,
}) => {
  const currentSegment = useRef("");
  const isStarted = useRef(false);
  const [isFinished, setFinished] = useState(false);
  const timerHandle = useRef(0);
  const angleCurrent = useRef(0);
  const angleDelta = useRef(0);
  const canvasContext = useRef(null);
  const maxSpeed = useRef(0);
  const spinStart = useRef(0);
  const frames = useRef(0);
  const progress = useRef(0);
  const winningSegment = useRef("");
  const soundRef = useRef();

  const logoImgRef = useRef(new Image());
  const frameImgRef = useRef(new Image());

  const timerDelay = segments.length + 45;
  const upTime = segments.length * upDuration;
  const downTime = segments.length * downDuration;
  const centerX = 300;
  const centerY = 300;

  const playActionSound = () => soundRef.current?.play();
  const stopActionSound = () => soundRef.current?.pause();

  useEffect(() => {
    logoImgRef.current.src = logoIcon;
    frameImgRef.current.src = frame;
    logoImgRef.current.onload = () => canvasContext.current && draw();
    frameImgRef.current.onload = () => canvasContext.current && draw();
  }, [logoIcon, frame]);

  useEffect(() => {
    wheelInit();
    setTimeout(() => window.scrollTo(0, 1), 0);
  }, []);

  const wheelInit = () => {
    initCanvas();
    wheelDraw();
  };

  const initCanvas = () => {
    let canvas = document.getElementById("canvas");
    let button = document.getElementById("button");

    if (navigator.userAgent.indexOf("MSIE") !== -1) {
      canvas = document.createElement("canvas");
      canvas.setAttribute("width", 1200);
      canvas.setAttribute("height", 800);
      canvas.setAttribute("id", "canvas");
      document.getElementById("wheel").appendChild(canvas);
    }

    if (result.length > 0) {
      button.addEventListener("click", spin, false);
    }

    canvasContext.current = canvas.getContext("2d");
  };

  const spin = () => {
    playActionSound();
    const randomIndex = Math.floor(Math.random() * result.length);
    const randomObject = result[randomIndex];
    const index = data?.findIndex((arr) => arr.id === randomObject.id);

    winningSegment.current = index + 1;
    isStarted.current = true;

    if (timerHandle.current === 0) {
      spinStart.current = new Date().getTime();
      maxSpeed.current = Math.PI / segments.length;
      frames.current = 0;
      timerHandle.current = setInterval(onTimerTick, timerDelay);
    }
  };

  const onTimerTick = () => {
    frames.current++;
    draw();
    const duration = new Date().getTime() - spinStart.current;
    let finished = false;

    if (duration < upTime) {
      progress.current = duration / upTime;
      angleDelta.current =
        maxSpeed.current *
        Math.sin((progress.current * Math.PI) / 2 + Math.PI / 2);
    } else {
      if (winningSegment.current) {
        if (
          currentSegment.current === winningSegment.current &&
          frames.current >= segments.length * 3 &&
          progress.current >= 0.65999
        ) {
          finished = true;
        } else {
          progress.current = duration / downTime;
          angleDelta.current =
            maxSpeed.current *
            Math.sin((progress.current * Math.PI) / 2 + Math.PI / 2);
        }
      }

      if (progress.current >= 1) finished = true;
    }

    angleCurrent.current += angleDelta.current;
    while (angleCurrent.current >= Math.PI * 2)
      angleCurrent.current -= Math.PI * 2;

    if (finished) {
      setFinished(true);
      onFinished(currentSegment.current - 1);
      clearInterval(timerHandle.current);
      timerHandle.current = 0;
      angleDelta.current = 0;
      progress.current = 0;
      stopActionSound();
    }
  };

  const wheelDraw = () => {
    clear();
    drawWheel();
    drawNeedle();
  };

  const draw = () => {
    clear();
    drawWheel();
    drawNeedle();

    const ctx = canvasContext.current;

    // ✅ Draw logo and frame last so they're on top
    if (frameImgRef.current.complete) {
      ctx.drawImage(frameImgRef.current, -3, 2, 600, 600); // previously 400x400
    }

    if (logoImgRef.current.complete) {
      ctx.drawImage(logoImgRef.current, 250, 250, 100, 100);
    }
  };

  const drawSegment = (key, lastAngle, angle) => {
    const ctx = canvasContext.current;
    const value = segImg[key];
    const newImg = new Image();
    newImg.src = value;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, size, lastAngle, angle, false);
    ctx.lineTo(centerX, centerY);
    ctx.closePath();
    ctx.fillStyle = segColors[key];
    ctx.fill();
    ctx.stroke();
    ctx.save();

    newImg.onload = () => {
      ctx.translate(centerX, centerY);
      ctx.rotate((lastAngle + angle) / 2);
      ctx.drawImage(newImg, imgX, imgY, imgSize, imgSize);
      ctx.restore();
    };
  };

  const drawWheel = () => {
    const ctx = canvasContext.current;
    let lastAngle = angleCurrent.current;
    const len = segments.length;
    const PI2 = Math.PI * 2;

    ctx.lineWidth = 1;
    ctx.strokeStyle = primaryColor;
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.font = "1em " + fontFamily;

    for (let i = 0; i <= len; i++) {
      const angle = PI2 * (i / len) + angleCurrent.current;
      drawSegment(i - 1, lastAngle, angle);
      lastAngle = angle;
    }

    ctx.beginPath();
    ctx.closePath();
    ctx.fillStyle = primaryColor;
    ctx.lineWidth = 10;
    ctx.strokeStyle = "white";
    ctx.fill();
    ctx.stroke();
  };

  const drawNeedle = () => {
    const ctx = canvasContext.current;
    ctx.lineWidth = 1;
    ctx.strokeStyle = "#000";
    ctx.fillStyle = "#000";
    ctx.beginPath();
    ctx.moveTo(centerX + 20, centerY - 10);
    ctx.lineTo(centerX - 20, centerY - 10);
    ctx.lineTo(centerX, centerY - 20);
    ctx.closePath();
    ctx.fill();

    const change = angleCurrent.current + Math.PI / 2;
    let i =
      segments.length -
      Math.floor((change / (Math.PI * 2)) * segments.length) -
      1;
    if (i < 0) i += segments.length;

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#000";
    ctx.font = "bold 1.5em " + fontFamily;
    currentSegment.current = i + 1;
  };

  const clear = () => {
    const ctx = canvasContext.current;
    ctx.clearRect(0, 0, 1000, 800);
  };

  return (
    <>
      <Box
        id="wheel"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: 6,
          py: 3,
          position: "relative",
          flexDirection: "column",
        }}
      >
        <audio ref={soundRef}>
          <source src={spinner} />
        </audio>

        <canvas
          id="canvas"
          width="600"
          height="600"
          style={{
            zIndex: 1,
            pointerEvents: isFinished && isOnlyOnce ? "none" : "auto",
          }}
        />
      </Box>

      <Button
        sx={{
          mt: 4,
          py: 1.8,
          fontSize: "18px",
          fontWeight: 500,
          borderRadius: "30px",
          textTransform: "none",
          backgroundColor: "#d89f84",
          "&:hover": { backgroundColor: "#c55c2bff" },
          maxWidth: "300px",
          width: 1,
        }}
        id="button"
        color="secondary"
        variant="contained"
      >
        <Typography
          sx={{
            color: "white",
            fontWeight: 700,
            fontSize: "25px",
            lineHeight: 1.2,
          }}
        >
          Start
        </Typography>
      </Button>
    </>
  );
};

export default WheelComponent;
